import React, { useState } from "react";
import toast from "../../components/ui/toast";
import { Helmet } from "react-helmet";
import PageFooter from "../../components/PageFooter/PageFooter";
import "./advancedSearch.css";

// Constants
const FILE_TYPES = [
  { value: "pdf", label: "PDF" },
  { value: "doc", label: "DOC" },
  { value: "docx", label: "DOCX" },
  { value: "xls", label: "XLS" },
  { value: "xlsx", label: "XLSX" },
  { value: "ppt", label: "PPT" },
  { value: "pptx", label: "PPTX" },
];

const SCRAPING_SCRIPT = `// Google Search Results Extractor - Version Simple et Efficace
// Run this script in Chrome console on a Google search results page

function extractGoogleSearchResults() {
    const results = [];
    const seenDomains = new Set();
    const searchResults = document.querySelectorAll('.tF2Cxc, .MjjYud .tF2Cxc');
    
    searchResults.forEach((result, index) => {
        try {
            const titleElement = result.querySelector('h3.LC20lb');
            const linkElement = result.querySelector('a[jsname="UWckNb"]') || result.querySelector('a[href]');
            const descriptionElement = result.querySelector('.VwiC3b') || result.querySelector('.p4wth');
            
            if (titleElement && linkElement) {
                const domain = extractDomainFromURL(linkElement.href);
                
                // Skip if we've already seen this domain
                if (seenDomains.has(domain)) {
                    console.log(\`Skipping duplicate domain: \${domain}\`);
                    return;
                }
                
                seenDomains.add(domain);
                const linkedinUrl = generateLinkedInSearchURL(domain, titleElement.textContent);
                
                const resultData = {
                    index: results.length + 1, // Use results length for proper indexing
                    title: titleElement.textContent.trim(),
                    url: linkElement.href,
                    description: descriptionElement ? descriptionElement.textContent.trim() : '',
                    domain: domain,
                    linkedinSearch: linkedinUrl
                };
                
                results.push(resultData);
            }
        } catch (error) {
            console.warn(\`Error processing result \${index + 1}:\`, error);
        }
    });
    
    return results;
}

function extractGoogleSearchResultsAlternative() {
    const results = [];
    const seenDomains = new Set();
    const selectors = ['.g .yuRUbf', '.srg .g', '[data-hveid] .yuRUbf', '.rc', '.tF2Cxc'];
    
    let searchResults = [];
    for (const selector of selectors) {
        searchResults = document.querySelectorAll(selector);
        if (searchResults.length > 0) {
            console.log(\`Using selector: \${selector} (found \${searchResults.length} results)\`);
            break;
        }
    }
    
    searchResults.forEach((result, index) => {
        try {
            const titleSelectors = ['h3.LC20lb', 'h3', '.LC20lb', '[role="heading"] h3'];
            const linkSelectors = ['a[jsname="UWckNb"]', 'a[href^="http"]', 'a[ping]', 'h3 a', 'a'];
            const descriptionSelectors = ['.VwiC3b', '.p4wth', '.s', '.st', '[data-snf="nke7rc"] div'];
            
            let titleElement = null;
            let linkElement = null;
            let descriptionElement = null;
            
            for (const selector of titleSelectors) {
                titleElement = result.querySelector(selector);
                if (titleElement) break;
            }
            
            for (const selector of linkSelectors) {
                linkElement = result.querySelector(selector);
                if (linkElement && linkElement.href && linkElement.href.startsWith('http')) break;
            }
            
            for (const selector of descriptionSelectors) {
                descriptionElement = result.querySelector(selector);
                if (descriptionElement && descriptionElement.textContent.trim()) break;
            }
            
            if (titleElement && linkElement) {
                const domain = extractDomainFromURL(linkElement.href);
                
                // Skip if we've already seen this domain
                if (seenDomains.has(domain)) {
                    console.log(\`Skipping duplicate domain: \${domain}\`);
                    return;
                }
                
                seenDomains.add(domain);
                const linkedinUrl = generateLinkedInSearchURL(domain, titleElement.textContent);
                
                results.push({
                    index: results.length + 1, // Use results length for proper indexing
                    title: titleElement.textContent.trim(),
                    url: linkElement.href,
                    description: descriptionElement ? descriptionElement.textContent.trim() : '',
                    domain: domain,
                    linkedinSearch: linkedinUrl
                });
            }
        } catch (error) {
            console.warn(\`Error processing alternative result \${index + 1}:\`, error);
        }
    });
    
    return results;
}

function extractDomainFromURL(url) {
    try {
        const urlObj = new URL(url);
        let domain = urlObj.hostname;
        domain = domain.replace(/^(www\\.|m\\.|mobile\\.|fr\\.|en\\.)/, '');
        return domain;
    } catch (error) {
        console.warn('Erreur extraction domaine:', error);
        return '';
    }
}

function generateLinkedInSearchURL(domain, title) {
    try {
        // Extract company name from domain (remove extension)
        let companyName = domain.replace(/\\.(com|fr|org|net|eu|co\\.uk|be|ch|de|it|es|ca|us)$/i, '');
        
        // Remove special characters and replace with spaces
        companyName = companyName.replace(/[-_.]/g, ' ');
        
        // Clean up extra spaces and trim
        companyName = companyName.replace(/\\s+/g, ' ').trim();
        
        // Encode for URL
        const encodedKeywords = encodeURIComponent(companyName);
        
        return \`https://www.linkedin.com/search/results/companies/?keywords=\${encodedKeywords}&origin=SWITCH_SEARCH_VERTICAL&sid=m*g\`;
        
    } catch (error) {
        console.warn('Error generating LinkedIn URL:', error);
        // Fallback to basic domain name
        const basicName = domain.replace(/\\.(com|fr|org|net|eu|co\\.uk|be|ch|de|it|es|ca|us)$/i, '');
        return \`https://www.linkedin.com/search/results/companies/?keywords=\${encodeURIComponent(basicName)}&origin=SWITCH_SEARCH_VERTICAL&sid=m*g\`;
    }
}

function getGoogleResults() {
    console.log('🔍 Extracting Google search results...');
    
    let results = extractGoogleSearchResults();
    
    if (results.length === 0) {
        console.log('⚠️ First method found no results, trying alternative method...');
        results = extractGoogleSearchResultsAlternative();
    }
    
    console.log(\`✅ Found \${results.length} unique search results (duplicates removed)\`);
    console.log('📊 Domains found:', results.map(r => r.domain).join(', '));
    console.table(results);
    console.log('\\n📋 JSON format (copy this):');
    console.log(JSON.stringify(results, null, 2));
    
    if (results.length > 0) {
        console.log('💾 Auto-downloading CSV file...');
        exportToCSV(results);
    }
    
    return results;
}

function exportToCSV(results) {
    if (!results || results.length === 0) {
        console.log('❌ No results to export');
        return;
    }
    
    const headers = ['Index', 'Title', 'URL', 'Description', 'Domain', 'LinkedIn_Search'];
    const csvContent = [
        headers.join(','),
        ...results.map(result => [
            result.index,
            \`"\${(result.title || '').replace(/"/g, '""')}"\`,
            \`"\${result.url || ''}"\`,
            \`"\${(result.description || '').replace(/"/g, '""')}"\`,
            \`"\${result.domain || ''}"\`,
            \`"\${result.linkedinSearch || ''}"\`
        ].join(','))
    ].join('\\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'google_search_results_unique.csv';
    link.style.display = 'none';
    
    document.body.appendChild(link);
    setTimeout(() => {
        link.click();
        console.log('📊 CSV file downloaded successfully with unique domains only');
        
        setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 100);
    }, 100);
}

const searchResults = getGoogleResults();

console.log('\\n🔧 Available functions:');
console.log('- getGoogleResults() - Extract unique results and auto-download CSV');
console.log('- exportToCSV(searchResults) - Manually export to CSV file');
console.log('- generateLinkedInSearchURL(domain, title) - Generate LinkedIn search URL');
console.log('- searchResults - Contains the extracted data (unique domains only)');

console.log('\\n💡 Tips for better results:');
console.log('- Duplicate domains are automatically removed to avoid redundancy');
console.log('- Only one result per domain is kept (useful for store locators with multiple cities)');
console.log('- The LinkedIn search uses domain name without special characters');
console.log('- Alternative: Use tools like Hunter.io, Apollo.io, or ZoomInfo for direct contact info');`;

const AdvancedSearch = () => {
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    searchTerms: "",
    exactPhrase: "",
    orTerms: "",
    site: "",
    inurl: "",
    intitle: "",
    filetype: "",
    excludeTerms: "",
  });

  // Helper function to process comma-separated terms
  const processTerms = (terms) => {
    if (!terms || !terms.length) return [];
    return typeof terms === "string"
      ? terms.split(",").filter((term) => term.trim())
      : terms;
  };

  // Build Google search query
  const buildSearchQuery = (values) => {
    const queryParts = [];

    // Basic search terms
    if (values.searchTerms) {
      const hasOperators =
        values.searchTerms.includes(" OR ") ||
        values.searchTerms.includes(" AND ");
      queryParts.push(
        hasOperators ? `(${values.searchTerms})` : values.searchTerms,
      );
    }

    // Exact phrase
    if (values.exactPhrase) {
      queryParts.push(`"${values.exactPhrase}"`);
    }

    // OR terms
    const orTermsArray = processTerms(values.orTerms);
    if (orTermsArray.length > 0) {
      const orQuery = orTermsArray
        .map((term) => `"${term.trim()}"`)
        .join(" OR ");
      queryParts.push(`(${orQuery})`);
    }

    // Search operators
    const operators = [
      { key: "site", value: values.site },
      { key: "inurl", value: values.inurl, quoted: true },
      { key: "intitle", value: values.intitle, quoted: true },
      { key: "filetype", value: values.filetype },
    ];

    operators.forEach(({ key, value, quoted }) => {
      if (value) {
        queryParts.push(`${key}:${quoted ? `"${value}"` : value}`);
      }
    });

    // Excluded terms
    const excludeTermsArray = processTerms(values.excludeTerms);
    excludeTermsArray.forEach((term) => {
      queryParts.push(`-"${term.trim()}"`);
    });

    return queryParts.join(" ").trim();
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const query = buildSearchQuery(formData);
    setSearchQuery(query);

    const encodedQuery = encodeURIComponent(query);
    const googleUrl = `https://www.google.com/search?q=${encodedQuery}&num=100`;
    setGeneratedUrl(googleUrl);
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Copy to clipboard utility
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      toast.success("Copié dans le presse-papiers !");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Erreur lors de la copie");
    }
  };

  // Copy scraping script
  const copyScrapingScript = () => {
    copyToClipboard(SCRAPING_SCRIPT);
  };

  // Open in Google
  const openInGoogle = () => {
    if (generatedUrl) {
      window.open(generatedUrl, "_blank");
    }
  };

  // Auto-fill form from industry card
  const handleIndustryCardClick = (industryData) => {
    const newFormData = {
      searchTerms: industryData.searchTerms || "",
      exactPhrase: "",
      orTerms: "",
      site: industryData.site || ".fr",
      inurl: industryData.inurl || "",
      intitle: "",
      filetype: "",
      excludeTerms: "",
    };

    setFormData(newFormData);

    // Auto-generate query
    const query = buildSearchQuery(newFormData);
    setSearchQuery(query);

    const encodedQuery = encodeURIComponent(query);
    const googleUrl = `https://www.google.com/search?q=${encodedQuery}&num=100`;
    setGeneratedUrl(googleUrl);

    // Show success message
    toast.success("Formulaire rempli automatiquement !");

    // Scroll to result card instead of form card
    setTimeout(() => {
      const resultCard = document.querySelector(".result-card");
      if (resultCard) {
        resultCard.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  return (
    <div className="advanced-search-container">
      <Helmet>
        <title>Extraction Recherche avancée Google | LocalRanker</title>
        <meta
          name="description"
          content="Construisez facilement des requêtes de recherche Google avancées avec des opérateurs spécialisés."
        />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="content-section">
        {/* Form Card */}
        <div className="card form-card">
          <div className="card__title">Constructeur de requête avancée</div>
          <form onSubmit={handleSubmit}>
            {/* Basic Search Fields */}
            <div className="form-group">
              <label className="form-label">
                Termes de recherche principaux
              </label>
              <input
                className="input input--lg"
                value={formData.searchTerms}
                onChange={(e) =>
                  handleInputChange("searchTerms", e.target.value)
                }
                placeholder='Ex: "centre auditif" OR "audioprothésiste" OR "appareil auditif"'
              />
              <div className="form-help">
                Entrez vos termes de recherche. Vous pouvez utiliser OR et AND
                pour combiner les termes.
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Phrase exacte</label>
              <input
                className="input input--lg"
                value={formData.exactPhrase}
                onChange={(e) =>
                  handleInputChange("exactPhrase", e.target.value)
                }
                placeholder="Ex: centre auditif"
              />
              <div className="form-help">
                Rechercher une phrase exacte entre guillemets
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Termes alternatifs (OR)</label>
              <input
                className="input input--lg"
                value={formData.orTerms}
                onChange={(e) => handleInputChange("orTerms", e.target.value)}
                placeholder="Séparez les termes par des virgules"
              />
              <div className="form-help">
                Ajoutez des termes alternatifs qui seront combinés avec OR
                (séparez par des virgules)
              </div>
            </div>

            <div className="divider--text">Restrictions de domaine et URL</div>

            {/* Domain and URL Fields */}
            <div className="form-group">
              <label className="form-label">Restriction de site</label>
              <div className="input--with-prefix">
                <span className="input__prefix">site:</span>
                <input
                  className="input input--lg"
                  value={formData.site}
                  onChange={(e) => handleInputChange("site", e.target.value)}
                  placeholder="Ex: .fr ou example.com"
                />
              </div>
              <div className="form-help">
                Limiter la recherche à un domaine spécifique
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">URL doit contenir</label>
              <div className="input--with-prefix">
                <span className="input__prefix">inurl:</span>
                <input
                  className="input input--lg"
                  value={formData.inurl}
                  onChange={(e) => handleInputChange("inurl", e.target.value)}
                  placeholder="Ex: /nos-centres"
                />
              </div>
              <div className="form-help">L'URL doit contenir ce texte</div>
            </div>

            <div className="form-group">
              <label className="form-label">Titre doit contenir</label>
              <div className="input--with-prefix">
                <span className="input__prefix">intitle:</span>
                <input
                  className="input input--lg"
                  value={formData.intitle}
                  onChange={(e) => handleInputChange("intitle", e.target.value)}
                  placeholder="Ex: centre auditif"
                />
              </div>
              <div className="form-help">
                Le titre de la page doit contenir ce texte
              </div>
            </div>

            {/* File Type and Exclusions */}
            <div className="form-group">
              <label className="form-label">Type de fichier</label>
              <select
                className="select"
                value={formData.filetype || ""}
                onChange={(e) => handleInputChange("filetype", e.target.value)}
              >
                <option value="">Sélectionnez un type de fichier</option>
                {FILE_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <div className="form-help">
                Rechercher un type de fichier spécifique
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Exclure ces termes</label>
              <input
                className="input input--lg"
                value={formData.excludeTerms}
                onChange={(e) =>
                  handleInputChange("excludeTerms", e.target.value)
                }
                placeholder="Séparez les termes par des virgules"
              />
              <div className="form-help">
                Termes à exclure des résultats (séparez par des virgules)
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-group">
              <button
                className="btn btn--primary btn--lg btn--block"
                type="submit"
              >
                Générer la requête
              </button>
            </div>
          </form>
        </div>

        {/* Results Card */}
        {searchQuery && (
          <div className="card result-card">
            <div className="card__title">Requête générée</div>
            <div className="query-result">
              <strong>Requête de recherche :</strong>
              <div className="query-display">
                <textarea
                  className="input"
                  value={searchQuery}
                  readOnly
                  rows={3}
                />
                <button
                  className="btn"
                  onClick={() => copyToClipboard(searchQuery)}
                  title="Copier la requête"
                >
                  Copier
                </button>
              </div>
            </div>

            <hr className="divider" />

            <div className="url-result">
              <strong>URL Google :</strong>
              <div className="url-display">
                <textarea
                  className="input"
                  value={generatedUrl}
                  readOnly
                  rows={3}
                />
                <div className="button-group">
                  <button
                    className="btn"
                    onClick={() => copyToClipboard(generatedUrl)}
                    title="Copier l'URL"
                  >
                    Copier
                  </button>
                  <button
                    className="btn btn--primary"
                    onClick={openInGoogle}
                    title="Ouvrir dans Google"
                  >
                    Rechercher
                  </button>
                </div>
              </div>
            </div>

            <hr className="divider" />

            {/* Scraping Section */}
            <div className="scraping-section">
              <strong>🤖 Script de Scraping Automatique</strong>
              <div className="scraping-help">
                Copiez ce script et collez-le dans la console Chrome (F12) sur
                la page de résultats Google pour extraire automatiquement tous
                les résultats en CSV.
              </div>
              <div className="scraping-button-container">
                <button
                  className="btn btn--primary btn--lg scraping-button"
                  onClick={copyScrapingScript}
                >
                  Copier script de scraping Google
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Example Card */}
        <div className="card example-card">
          <div className="card__title">Exemple d'utilisation</div>
          <p>
            <strong>Exemple :</strong> Pour rechercher des centres auditifs en
            France, vous pourriez utiliser :
          </p>
          <ul>
            <li>
              <strong>Termes de recherche :</strong> "centre auditif" OR
              "audioprothésiste" OR "appareil auditif"
            </li>
            <li>
              <strong>URL doit contenir :</strong> /nos-centres
            </li>
            <li>
              <strong>Restriction de site :</strong> .fr
            </li>
          </ul>
          <p>
            <strong>Résultat :</strong>{" "}
            <code>
              inurl:"/nos-centres" ("centre auditif" OR "audioprothésiste" OR
              "appareil auditif") site:.fr
            </code>
          </p>
        </div>

        {/* Industry Terms Guide Card */}
        <div className="card industry-guide-card">
          <div className="card__title">🏪 Guide des termes par industrie</div>
          <div className="industry-section">
            <h4>Termes génériques pour points de vente</h4>
            <div className="terms-grid">
              <div className="terms-category">
                <strong>Localisation physique :</strong>
                <div className="terms-list">
                  "nos magasins", "nos boutiques", "nos points de vente", "nos
                  centres", "nos agences", "nos showrooms", "trouver un
                  magasin", "store locator", "où nous trouver", "nos adresses"
                </div>
              </div>

              <div className="terms-category">
                <strong>URLs communes :</strong>
                <div className="terms-list">
                  /magasins, /boutiques, /stores, /points-de-vente, /agences,
                  /centres, /showrooms, /localisateur, /ou-nous-trouver,
                  /adresses
                </div>
              </div>
            </div>
          </div>

          <hr className="divider" />

          <div className="industry-section">
            <h4>Par secteur d'activité</h4>
            <div className="industry-grid">
              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms:
                      '"nos magasins" OR "hypermarché" OR "supermarché" OR "drive"',
                    inurl: "/magasins",
                  })
                }
              >
                <strong>🛒 Grande distribution</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "hypermarché", "supermarché",
                    "magasin", "drive", "courses en ligne"
                  </div>
                  <div>
                    <code>URLs :</code> /magasins, /drive, /courses, /livraison
                  </div>
                  <div>
                    <code>Exemple :</code> "nos magasins" OR "drive" OR "courses
                    en ligne" inurl:"/magasins" site:.fr
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos boutiques" OR "magasin" OR "showroom"',
                    inurl: "/boutiques",
                  })
                }
              >
                <strong>👗 Mode & Textile</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "boutique", "magasin", "showroom",
                    "collection", "essayage"
                  </div>
                  <div>
                    <code>URLs :</code> /boutiques, /magasins, /stores,
                    /showroom, /collection
                  </div>
                  <div>
                    <code>Exemple :</code> "nos boutiques" OR "showroom"
                    inurl:"/boutiques" filetype:html
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms:
                      '"nos pharmacies" OR "officine" OR "parapharmacie"',
                    inurl: "/pharmacies",
                  })
                }
              >
                <strong>🏥 Santé & Pharmacie</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "pharmacie", "officine",
                    "parapharmacie", "centre de santé", "cabinet"
                  </div>
                  <div>
                    <code>URLs :</code> /pharmacies, /officines, /centres,
                    /cabinets, /sante
                  </div>
                  <div>
                    <code>Exemple :</code> "nos pharmacies" OR "officine"
                    inurl:"/pharmacies" site:.fr
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms:
                      '"nos restaurants" OR "franchise" OR "nos établissements"',
                    inurl: "/restaurants",
                  })
                }
              >
                <strong>🍕 Restauration</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "nos restaurants", "franchise", "nos
                    établissements", "chaîne", "enseigne"
                  </div>
                  <div>
                    <code>URLs :</code> /restaurants, /etablissements,
                    /franchise, /chaine, /enseigne
                  </div>
                  <div>
                    <code>Exemple :</code> "nos restaurants" OR "franchise"
                    inurl:"/restaurants" OR inurl:"/etablissements"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms:
                      '"nos agences bancaires" OR "succursale" OR "agence"',
                    inurl: "/agences",
                  })
                }
              >
                <strong>🏦 Banque & Finance</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "agence bancaire", "succursale",
                    "bureau", "centre financier", "point banque"
                  </div>
                  <div>
                    <code>URLs :</code> /agences, /succursales, /bureaux,
                    /centres, /banque
                  </div>
                  <div>
                    <code>Exemple :</code> "nos agences bancaires" OR
                    "succursale" inurl:"/agences" site:banque
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos magasins" OR "centre" OR "jardinerie"',
                    inurl: "/magasins",
                  })
                }
              >
                <strong>🔧 Bricolage & Jardinage</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "magasin", "centre", "jardinerie",
                    "bricolage", "outillage"
                  </div>
                  <div>
                    <code>URLs :</code> /magasins, /centres, /jardinerie,
                    /bricolage, /outillage
                  </div>
                  <div>
                    <code>Exemple :</code> "nos magasins" OR "centre"
                    inurl:"/magasins" OR inurl:"/centres"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos concessions" OR "garage" OR "atelier"',
                    inurl: "/concessions",
                  })
                }
              >
                <strong>🚗 Automobile</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "concession", "garage", "atelier",
                    "service", "pièces"
                  </div>
                  <div>
                    <code>URLs :</code> /concessions, /garages, /ateliers,
                    /service, /pieces
                  </div>
                  <div>
                    <code>Exemple :</code> "nos concessions" OR "garage"
                    inurl:"/concessions" OR inurl:"/garages"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos instituts" OR "salon" OR "spa"',
                    inurl: "/instituts",
                  })
                }
              >
                <strong>💄 Beauté & Cosmétiques</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "institut", "salon", "spa", "beauté",
                    "soin"
                  </div>
                  <div>
                    <code>URLs :</code> /instituts, /salons, /spa, /beaute,
                    /soins
                  </div>
                  <div>
                    <code>Exemple :</code> "nos instituts" OR "salon"
                    inurl:"/instituts" OR inurl:"/salons"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms:
                      '"nos clubs" OR "salle de sport" OR "centre fitness"',
                    inurl: "/clubs",
                  })
                }
              >
                <strong>⚽ Sport & Fitness</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "salle de sport", "club", "centre
                    fitness", "piscine", "terrain"
                  </div>
                  <div>
                    <code>URLs :</code> /clubs, /salles, /centres, /fitness,
                    /sport
                  </div>
                  <div>
                    <code>Exemple :</code> "nos clubs" OR "salle de sport"
                    inurl:"/clubs" OR inurl:"/salles"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos magasins" OR "réparation" OR "SAV"',
                    inurl: "/magasins",
                  })
                }
              >
                <strong>📱 Électronique & High-Tech</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "magasin", "boutique", "showroom",
                    "réparation", "SAV"
                  </div>
                  <div>
                    <code>URLs :</code> /magasins, /boutiques, /reparation,
                    /sav, /support
                  </div>
                  <div>
                    <code>Exemple :</code> "nos magasins" OR "réparation"
                    inurl:"/magasins" OR inurl:"/reparation"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos boulangeries" OR "pâtisserie"',
                    inurl: "/boulangeries",
                  })
                }
              >
                <strong>🍞 Boulangerie & Pâtisserie</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "boulangerie", "pâtisserie", "nos
                    adresses", "magasin", "boutique"
                  </div>
                  <div>
                    <code>URLs :</code> /boulangeries, /patisseries, /magasins,
                    /adresses, /boutiques
                  </div>
                  <div>
                    <code>Exemple :</code> "nos boulangeries" OR "pâtisserie"
                    inurl:"/boulangeries" site:.fr
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms:
                      '"nos écoles" OR "campus" OR "centre de formation"',
                    inurl: "/ecoles",
                  })
                }
              >
                <strong>🎓 Éducation & Formation</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "école", "centre de formation",
                    "campus", "établissement", "antenne"
                  </div>
                  <div>
                    <code>URLs :</code> /ecoles, /centres, /campus, /formations,
                    /etablissements
                  </div>
                  <div>
                    <code>Exemple :</code> "nos écoles" OR "campus"
                    inurl:"/ecoles" OR inurl:"/campus"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos agences" OR "bureau" OR "implantations"',
                    inurl: "/agences",
                  })
                }
              >
                <strong>🏠 Immobilier</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "agence", "bureau", "antenne", "nos
                    implantations", "contact"
                  </div>
                  <div>
                    <code>URLs :</code> /agences, /bureaux, /antennes,
                    /implantations, /contact
                  </div>
                  <div>
                    <code>Exemple :</code> "nos agences" OR "bureau"
                    inurl:"/agences" OR inurl:"/bureaux"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms:
                      '"nos agences" OR "conseiller" OR "réservation"',
                    inurl: "/agences",
                  })
                }
              >
                <strong>✈️ Voyage & Tourisme</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "agence", "bureau", "point de vente",
                    "conseiller", "réservation"
                  </div>
                  <div>
                    <code>URLs :</code> /agences, /bureaux, /points-vente,
                    /conseillers, /contact
                  </div>
                  <div>
                    <code>Exemple :</code> "nos agences" OR "conseiller"
                    inurl:"/agences" OR inurl:"/conseillers"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos animaleries" OR "clinique vétérinaire"',
                    inurl: "/animaleries",
                  })
                }
              >
                <strong>🐾 Animalerie & Vétérinaire</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "animalerie", "clinique", "cabinet",
                    "magasin", "centre"
                  </div>
                  <div>
                    <code>URLs :</code> /animaleries, /cliniques, /cabinets,
                    /magasins, /centres
                  </div>
                  <div>
                    <code>Exemple :</code> "nos animaleries" OR "clinique"
                    inurl:"/animaleries" OR inurl:"/cliniques"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos librairies" OR "papeterie"',
                    inurl: "/librairies",
                  })
                }
              >
                <strong>📚 Librairie & Papeterie</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "librairie", "papeterie", "magasin",
                    "boutique", "espace"
                  </div>
                  <div>
                    <code>URLs :</code> /librairies, /papeteries, /magasins,
                    /boutiques, /espaces
                  </div>
                  <div>
                    <code>Exemple :</code> "nos librairies" OR "papeterie"
                    inurl:"/librairies" OR inurl:"/papeteries"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos magasins" OR "école de musique"',
                    inurl: "/magasins",
                  })
                }
              >
                <strong>🎵 Musique & Instruments</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "magasin", "boutique", "showroom",
                    "école", "atelier"
                  </div>
                  <div>
                    <code>URLs :</code> /magasins, /boutiques, /showroom,
                    /ecoles, /ateliers
                  </div>
                  <div>
                    <code>Exemple :</code> "nos magasins" OR "école"
                    inurl:"/magasins" OR inurl:"/ecoles"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos magasins" OR "showroom" OR "exposition"',
                    inurl: "/magasins",
                  })
                }
              >
                <strong>🏡 Décoration & Mobilier</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "magasin", "showroom", "exposition",
                    "boutique", "espace"
                  </div>
                  <div>
                    <code>URLs :</code> /magasins, /showrooms, /expositions,
                    /boutiques, /espaces
                  </div>
                  <div>
                    <code>Exemple :</code> "nos magasins" OR "showroom"
                    inurl:"/magasins" OR inurl:"/showrooms"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos stations" OR "station-service"',
                    inurl: "/stations",
                  })
                }
              >
                <strong>⛽ Stations-service & Carburant</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "station", "station-service", "point
                    de vente", "distributeur", "nos stations"
                  </div>
                  <div>
                    <code>URLs :</code> /stations, /stations-service,
                    /points-vente, /distributeurs, /carburant
                  </div>
                  <div>
                    <code>Exemple :</code> "nos stations" OR "station-service"
                    inurl:"/stations" site:.fr
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms:
                      '"nos laboratoires" OR "centre de prélèvement"',
                    inurl: "/laboratoires",
                  })
                }
              >
                <strong>🏥 Laboratoires & Analyses</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "laboratoire", "centre", "point de
                    prélèvement", "antenne", "site"
                  </div>
                  <div>
                    <code>URLs :</code> /laboratoires, /centres, /prelevements,
                    /antennes, /sites
                  </div>
                  <div>
                    <code>Exemple :</code> "nos laboratoires" OR "centre"
                    inurl:"/laboratoires" OR inurl:"/centres"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos agences" OR "espace client"',
                    inurl: "/agences",
                  })
                }
              >
                <strong>🔌 Énergie & Électricité</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "agence", "espace client", "boutique",
                    "point d'accueil", "centre"
                  </div>
                  <div>
                    <code>URLs :</code> /agences, /espaces-clients, /boutiques,
                    /accueil, /centres
                  </div>
                  <div>
                    <code>Exemple :</code> "nos agences" OR "espace client"
                    inurl:"/agences" OR inurl:"/espaces-clients"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos agences" OR "dépôt" OR "plateforme"',
                    inurl: "/agences",
                  })
                }
              >
                <strong>🚛 Transport & Logistique</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "agence", "dépôt", "plateforme",
                    "centre", "antenne"
                  </div>
                  <div>
                    <code>URLs :</code> /agences, /depots, /plateformes,
                    /centres, /antennes
                  </div>
                  <div>
                    <code>Exemple :</code> "nos agences" OR "dépôt"
                    inurl:"/agences" OR inurl:"/depots"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"nos centres" OR "complexe" OR "parc"',
                    inurl: "/centres",
                  })
                }
              >
                <strong>🎪 Loisirs & Divertissement</strong>
                <div className="industry-terms">
                  <div>
                    <code>Termes :</code> "centre", "parc", "complexe", "salle",
                    "espace"
                  </div>
                  <div>
                    <code>URLs :</code> /centres, /parcs, /complexes, /salles,
                    /espaces
                  </div>
                  <div>
                    <code>Exemple :</code> "nos centres" OR "complexe"
                    inurl:"/centres" OR inurl:"/complexes"
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>

              <div
                className="industry-item"
                onClick={() =>
                  handleIndustryCardClick({
                    searchTerms: '"franchise" OR "réseau" OR "partenaire"',
                    inurl: "/franchise",
                  })
                }
              >
                <strong>🎯 Termes spécifiques aux réseaux</strong>
                <div className="industry-terms">
                  <div>
                    <code>Franchise :</code> "franchise", "franchisé", "réseau",
                    "partenaire", "enseigne"
                  </div>
                  <div>
                    <code>Localisation :</code> "store locator", "trouver un
                    magasin", "où nous trouver", "nos adresses"
                  </div>
                  <div>
                    <code>Exemple :</code> "franchise" OR "réseau"
                    inurl:"/franchise" OR inurl:"/reseau" site:.fr
                  </div>
                </div>
                <div className="click-hint">
                  👆 Cliquez pour remplir le formulaire
                </div>
              </div>
            </div>
          </div>

          <hr className="divider" />

          <div className="quick-tips">
            <h4>💡 Conseils rapides</h4>
            <ul>
              <li>
                <strong>Combinez les termes :</strong> Utilisez OR entre
                différents termes du même secteur
              </li>
              <li>
                <strong>Filtrez par extension :</strong> Ajoutez site:.fr pour
                la France, site:.com pour international
              </li>
              <li>
                <strong>Cherchez dans les URLs :</strong> inurl:"/magasins"
                trouve les pages dédiées aux points de vente
              </li>
              <li>
                <strong>Excluez les concurrents :</strong> Utilisez
                -"concurrent" pour exclure certaines marques
              </li>
              <li>
                <strong>Ciblez les formats :</strong> filetype:pdf pour les
                catalogues, filetype:html pour les pages web
              </li>
            </ul>
          </div>
        </div>
      </div>
      <PageFooter />
    </div>
  );
};

export default AdvancedSearch;
